import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FiLink } from 'react-icons/fi';

import { KANBAN_STATES } from 'constants';
import { BsFillCircleFill } from 'react-icons/bs';
import { getUserImage } from 'helpers';

// Unified status colors matching the app-wide color scheme
const STATUS_COLORS = {
  not_started: '#667085',
  in_progress: '#2E90FA',
  in_review: '#9333EA',
  approved: '#12B76A',
  scheduled: '#F59E0B',
  published: '#06B6D4',
};
import { mapTaskAttributes } from 'helpers/task';
import Drawer from 'Components/Drawer/Drawer';
import ImageView from 'Components/Image/ImageView';
import AIPoweredBrief from './AIPoweredBrief/AIPoweredBrief';
import BriefForm from './BriefForm';
import useProjects from 'hooks/projects/useProjects';
import useProjectUsers from 'hooks/projects/useProjectUsers';
import useInfiniteDocuments from 'hooks/documents/useInfiniteDocuments';

const BriefDrawer = ({
  onSubmit,
  onDeleteHandler,
  action,
  task,
  isProjectView = false,
  isAIPowered = true,
  show,
  onHide,
  disableCreateDraft = false,
  regenerate = false,
  regenerateFn,
}) => {
  const [searchDocs, setSearchDocs] = useState('');
  const [selectedProject, setSelectedProject] = useState('');

  const { user } = useSelector((state) => state.auth);

  const { projectId } = useParams();

  const { data: projectPages } = useProjects({ searchTerm: '' });
  const { data: projectUsers } = useProjectUsers({
    projectId: projectId || selectedProject,
  });

  const projects = useMemo(() => {
    return projectPages?.pages.flatMap((page) => page?.data?.projects);
  }, [projectPages?.pages]);

  const { document_id: documentId } = task || {};

  const {
    data: documentsPage,
    isLoading: isLoadingDocuments,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteDocuments({
    projectId: selectedProject || projectId || '',
    type: 'all',
    search: searchDocs,
  });

  const libraryDocuments = useMemo(() => {
    return documentsPage?.pages.flatMap((page) => page?.data?.documents);
  }, [documentsPage?.pages]);

  const documents = useMemo(
    () =>
      libraryDocuments?.filter(
        (document) => !document.task_associated || document.id === documentId,
      ),
    [libraryDocuments],
  );

  const users = useMemo(() => {
    if (projectId || selectedProject) {
      return (projectUsers?.data?.project_users || []).map(({ user }) => user);
    } else {
      return [user];
    }
  }, [projectId, selectedProject]);

  const userOptions = useMemo(() => {
    if (!users?.length) {
      return [];
    }

    return users.map((user) => {
      return {
        value: user.id,
        label: (
          <div className="d-flex align-items-center gap-3">
            {user.mini_avatar ? (
              <ImageView src={user.mini_avatar} class="rounded-profile-x-sm" />
            ) : (
              getUserImage(
                user.mini_avatar,
                user.name,
                { width: 32, height: 32 },
                'sm',
              )
            )}
            <p>{user.name}</p>
          </div>
        ),
      };
    });
  }, [users]);

  const documentOptions = useMemo(() => {
    if (isLoadingDocuments) {
      return [];
    }
    if (!documents?.length && hasNextPage) {
      fetchNextPage();
      return [];
    }

    return documents.map((document) => {
      return {
        value: document.id,
        label: (
          <div className="d-flex align-items-center gap-3">
            <FiLink size={20} color="#667085" />
            <p>{document.title}</p>
          </div>
        ),
      };
    });
  }, [documents, hasNextPage, isLoadingDocuments]);

  const teamOptions = useMemo(() => {
    if (!projects?.length) {
      return [];
    }

    return projects.map((project) => {
      return {
        value: project.id,
        label: (
          <div className="d-flex align-items-center gap-3">
            {project.mini_avatar ? (
              <ImageView
                src={project.mini_avatar}
                class="rounded-profile-x-sm"
              />
            ) : (
              getUserImage(
                project.mini_avatar,
                project.name,
                { width: 32, height: 32 },
                'sm',
              )
            )}
            <p>{project.name}</p>
          </div>
        ),
      };
    });
  }, [projects]);

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  const stateOptions = KANBAN_STATES.map((type) => {
    return {
      value: type,
      label: type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      icon: <BsFillCircleFill size={10} color={STATUS_COLORS[type] || '#667085'} />,
    };
  });

  useEffect(() => {
    setSelectedProject(task?.project_id || '');
  }, [task?.project_id]);

  const initialValues = useMemo(() => {
    return mapTaskAttributes(task, projectId);
  }, [task]);

  const modalTitle = action === 'create' ? 'Create New Brief' : 'Edit Brief';
  const buttonTitle = action === 'create' ? 'Add to Plan' : 'Update Brief';

  return (
    <Drawer show={show} onHide={onHide} title={modalTitle} placement="end">
      {action === 'edit' ? (
        <BriefForm
          initialValues={initialValues}
          priorityOptions={priorityOptions}
          userOptions={userOptions}
          stateOptions={stateOptions}
          teamOptions={teamOptions}
          documentOptions={documentOptions}
          isProjectView={isProjectView}
          setSelectedProject={setSelectedProject}
          buttonTitle={buttonTitle}
          action={action}
          onSubmit={onSubmit}
          onDeleteHandler={onDeleteHandler}
          taskId={task?.id}
          onHide={onHide}
          setSearchDocs={setSearchDocs}
          disableCreateDraft={disableCreateDraft}
          regenerate={regenerate}
          regenerateFn={regenerateFn}
        />
      ) : (
        <AIPoweredBrief
          initialValues={initialValues}
          priorityOptions={priorityOptions}
          userOptions={userOptions}
          stateOptions={stateOptions}
          isProjectView={isProjectView}
          teamOptions={teamOptions}
          setSelectedProject={setSelectedProject}
          documentOptions={documentOptions}
          buttonTitle={buttonTitle}
          onSubmit={onSubmit}
          onHide={onHide}
          setSearchDocs={setSearchDocs}
          action={action}
        />
      )}
    </Drawer>
  );
};

export default BriefDrawer;
